'use client';

import { useState } from 'react';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface ProjectCardProps {
    project: {
        id: number;
        title: string;
        category: string;
        description: string;
        image: string;
        alt: string;
        liveUrl: string;
        metrics: {
            trafficIncrease: string;
            rankingImprovement: string;
            conversionBoost: string;
        };
        technologies: string[];
    };
    onViewDetails: (id: number) => void;
}

export default function ProjectCard({ project, onViewDetails }: ProjectCardProps) {
    const [isHovered, setIsHovered] = useState(false);

    return (
        <div
            className="group relative bg-card border border-border rounded-xl overflow-hidden transition-all duration-300 hover:shadow-xl hover:-translate-y-1"
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            <div className="relative h-64 overflow-hidden">
                <AppImage
                    src={project.image}
                    alt={project.alt}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

                <div className={`absolute top-4 right-4 flex gap-2 transition-all duration-300 ${isHovered ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-2'}`}>
                    <a
                        href={project.liveUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2 bg-white/90 hover:bg-white rounded-lg transition-colors duration-200"
                        aria-label={`View ${project.title} live website`}
                    >
                        <Icon name="ArrowTopRightOnSquareIcon" size={20} className="text-brand-primary" />
                    </a>
                </div>

                <div className="absolute bottom-4 left-4 right-4">
                    <span className="inline-block px-3 py-1 bg-brand-accent text-brand-accent-foreground text-xs font-semibold rounded-full">
                        {project.category}
                    </span>
                </div>
            </div>

            <div className="p-6">
                <h3 className="text-xl font-bold text-text-primary mb-2 line-clamp-1">
                    {project.title}
                </h3>
                <p className="text-sm text-text-secondary mb-4 line-clamp-2">
                    {project.description}
                </p>

                <div className="grid grid-cols-3 gap-3 mb-4">
                    <div className="text-center p-2 bg-muted rounded-lg">
                        <div className="text-lg font-bold text-success">{project.metrics.trafficIncrease}</div>
                        <div className="text-xs text-text-secondary">Traffic</div>
                    </div>
                    <div className="text-center p-2 bg-muted rounded-lg">
                        <div className="text-lg font-bold text-brand-primary">{project.metrics.rankingImprovement}</div>
                        <div className="text-xs text-text-secondary">Ranking</div>
                    </div>
                    <div className="text-center p-2 bg-muted rounded-lg">
                        <div className="text-lg font-bold text-brand-accent">{project.metrics.conversionBoost}</div>
                        <div className="text-xs text-text-secondary">Conversion</div>
                    </div>
                </div>

                <div className="flex flex-wrap gap-2 mb-4">
                    {project.technologies.slice(0, 3).map((tech, index) => (
                        <span
                            key={index}
                            className="px-2 py-1 bg-secondary/10 text-secondary text-xs font-medium rounded"
                        >
                            {tech}
                        </span>
                    ))}
                    {project.technologies.length > 3 && (
                        <span className="px-2 py-1 bg-muted text-text-secondary text-xs font-medium rounded">
                            +{project.technologies.length - 3}
                        </span>
                    )}
                </div>

                <button
                    onClick={() => onViewDetails(project.id)}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-brand-primary text-brand-primary-foreground font-semibold rounded-lg hover:bg-brand-primary/90 transition-colors duration-200"
                >
                    <span>View Case Study</span>
                    <Icon name="ArrowRightIcon" size={16} />
                </button>
            </div>
        </div>
    );
}